/* eslint-disable jsdoc/require-jsdoc, no-console, no-alert */
(function stockStatusIntegration() {
  const integrationVersion = '1.1.0';
  const rootUrl = window.Shopify?.routes?.root || '/';
  const snapshotUrl = `${rootUrl}?section_id=at-cart-stock-data`;
  let cartSyncPromise = null;
  let cartSyncRequested = false;
  let shouldRefreshCart = false;
  let syncState = 'idle';
  let domCartSyncTimer = null;
  const checkoutBypass = new WeakSet();
  const acceleratedCheckoutState = new WeakMap();

  function setSyncState(state) {
    const hasInvalidLeadDays = Boolean(document.querySelector('form[data-lead-days-invalid]'));
    const lockAcceleratedCheckout = state === 'pending' || state === 'failed' || hasInvalidLeadDays;
    syncState = state;
    document.documentElement.toggleAttribute('data-stock-sync-pending', state === 'pending');
    document.documentElement.toggleAttribute('data-stock-sync-failed', state === 'failed');

    document.querySelectorAll('.additional-checkout-buttons').forEach((checkout) => {
      if (lockAcceleratedCheckout) {
        if (!acceleratedCheckoutState.has(checkout)) acceleratedCheckoutState.set(checkout, checkout.inert);
        checkout.toggleAttribute('inert', true);
        checkout.setAttribute('aria-disabled', 'true');
      } else if (acceleratedCheckoutState.has(checkout)) {
        checkout.toggleAttribute('inert', acceleratedCheckoutState.get(checkout));
        checkout.removeAttribute('aria-disabled');
        acceleratedCheckoutState.delete(checkout);
      }
    });
  }

  function dispatchSyncEvent(name, detail = {}) {
    document.documentElement.dispatchEvent(
      new CustomEvent(`stock-status:${name}`, {
        bubbles: true,
        detail: { version: integrationVersion, ...detail },
      })
    );
  }

  function quantityForForm(form) {
    const quantityInput = form.querySelector('input[name="quantity"], .quantity-selector__input');
    return Math.max(1, Number.parseInt(quantityInput?.value || '1', 10) || 1);
  }

  function leadDaysForStatus(stockStatus, productionLeadDays) {
    if (stockStatus !== 'backorder') return '5';

    const normalizedLeadDays = String(productionLeadDays || '').trim();
    return /^[1-9]\d*$/.test(normalizedLeadDays) ? normalizedLeadDays : '';
  }

  function refreshProductLeadDaysState() {
    document.documentElement.toggleAttribute('data-lead-days-invalid', Boolean(document.querySelector('form[data-lead-days-invalid]')));
    setSyncState(syncState);
  }

  function updateProductForm(form) {
    if (!form?.matches?.('form[action*="/cart/add"]')) return;

    const stockInput = form.querySelector('[data-stock-status-property]');
    const leadDaysInput = form.querySelector('[data-lead-days-property]');
    if (!stockInput || !leadDaysInput) return;

    const inventoryManaged = stockInput.dataset.inventoryManaged === 'true';
    const availableQuantity = Number.parseInt(stockInput.dataset.availableQty || '0', 10) || 0;
    const unmanagedStatus = stockInput.dataset.unmanagedStatus || 'in-stock';
    if (inventoryManaged) {
      stockInput.value = quantityForForm(form) > availableQuantity ? 'backorder' : 'in-stock';
    } else {
      stockInput.value = unmanagedStatus;
    }

    leadDaysInput.value = leadDaysForStatus(stockInput.value, leadDaysInput.dataset.productionLeadDays);
    form.toggleAttribute('data-lead-days-invalid', stockInput.value === 'backorder' && leadDaysInput.value === '');
    refreshProductLeadDaysState();
  }

  function formFromEvent(event) {
    if (event.target?.matches?.('form[action*="/cart/add"]')) return event.target;
    return event.target?.closest?.('form[action*="/cart/add"]') || null;
  }

  function normalizeLineProperties(value) {
    const sourceEntries = Array.isArray(value) ? value : Object.entries(value || {});
    const normalizedEntries = [];
    let needsCleanup = false;

    sourceEntries.forEach((entry) => {
      if (!Array.isArray(entry) || entry.length < 2) {
        needsCleanup = true;
        return;
      }

      const [key, propertyValue] = entry;

      // Liquid serializes line-item properties as key/value pairs. Older versions of this integration spread
      // that array into an object, producing properties such as "0": ["_stock_status", "in-stock"].
      if (/^\d+$/.test(String(key)) && Array.isArray(propertyValue) && propertyValue.length >= 2) {
        normalizedEntries.push([String(propertyValue[0]), propertyValue[1]]);
        needsCleanup = true;
      } else {
        normalizedEntries.push([String(key), propertyValue]);
      }
    });

    return { properties: Object.fromEntries(normalizedEntries), needsCleanup };
  }

  async function getStockSnapshot() {
    const response = await fetch(snapshotUrl, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      cache: 'no-store',
    });

    if (!response.ok) throw new Error(`Stock snapshot request failed (${response.status})`);

    const html = await response.text();
    const documentFragment = new DOMParser().parseFromString(html, 'text/html');
    const snapshot = documentFragment.querySelector('[data-at-cart-stock-snapshot]');

    if (!snapshot) throw new Error('Stock snapshot is missing from the section response');

    return Array.from(snapshot.querySelectorAll('[data-at-stock-line]')).map((element) => {
      let parsedProperties = {};

      try {
        parsedProperties = JSON.parse(element.dataset.properties || '{}') || {};
      } catch (error) {
        console.error('Invalid cart line properties in stock snapshot:', error);
      }

      const { properties, needsCleanup } = normalizeLineProperties(parsedProperties);

      return {
        key: element.dataset.lineKey,
        quantity: Number.parseInt(element.dataset.quantity || '0', 10) || 0,
        expectedStatus: element.dataset.expectedStatus,
        currentStatus: element.dataset.currentStatus,
        expectedLeadDays: element.dataset.expectedLeadDays,
        currentLeadDays: element.dataset.currentLeadDays,
        properties,
        needsCleanup,
      };
    });
  }

  async function updateFirstMismatchedLine() {
    const lines = await getStockSnapshot();
    const invalidLine = lines.find(({ expectedStatus, expectedLeadDays }) => expectedStatus === 'backorder' && !/^[1-9]\d*$/.test(expectedLeadDays));

    if (invalidLine) throw new Error('Backorder product is missing custom.max_delivery_days');

    const line = lines.find(
      ({ currentStatus, expectedStatus, currentLeadDays, expectedLeadDays, needsCleanup }) =>
        currentStatus !== expectedStatus || currentLeadDays !== expectedLeadDays || needsCleanup
    );

    if (!line) return false;

    const properties = {
      ...line.properties,
      _stock_status: line.expectedStatus,
      _lead_days: line.expectedLeadDays,
    };
    const response = await fetch(`${rootUrl}cart/change.js`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify({ id: line.key, quantity: line.quantity, properties }),
    });

    if (!response.ok) throw new Error(`Stock property update failed (${response.status})`);
    return true;
  }

  async function reconcileCart() {
    let changed = false;

    // A property update changes the line key and can merge lines. Fetch a fresh server snapshot after every write.
    for (let attempt = 0; attempt < 250; attempt += 1) {
      // eslint-disable-next-line no-await-in-loop
      const updated = await updateFirstMismatchedLine();
      if (!updated) return changed;
      changed = true;
    }

    throw new Error('Stock property reconciliation exceeded the cart line limit');
  }

  function requestCartSync({ refresh = true } = {}) {
    cartSyncRequested = true;
    shouldRefreshCart ||= refresh;

    if (cartSyncPromise) return cartSyncPromise;

    setSyncState('pending');

    cartSyncPromise = (async () => {
      let changed = false;

      try {
        while (cartSyncRequested) {
          cartSyncRequested = false;
          // eslint-disable-next-line no-await-in-loop
          changed = (await reconcileCart()) || changed;
        }

        if (changed && shouldRefreshCart) {
          if (window.themeVariables?.settings?.pageType === 'cart') {
            // Updating line properties changes Shopify's line key. Prestige reloads after quantity changes on the
            // cart page, but our property reconciliation happens just after that render. Reload once more so the
            // quantity controls receive the new key instead of submitting the stale one.
            window.location.reload();
          } else {
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          }
        }
        setSyncState('ready');
        dispatchSyncEvent('ready', { changed });
      } catch (error) {
        setSyncState('failed');
        dispatchSyncEvent('error', { message: error instanceof Error ? error.message : String(error) });
        throw error;
      } finally {
        shouldRefreshCart = false;
        cartSyncPromise = null;
      }
    })();

    return cartSyncPromise;
  }

  function isCheckoutSubmission(event) {
    const submitter = event.submitter || document.activeElement;
    return submitter?.name === 'checkout' && event.target?.matches?.('form[action*="/cart"]');
  }

  document.addEventListener('input', (event) => updateProductForm(formFromEvent(event)));
  document.addEventListener('change', (event) => updateProductForm(formFromEvent(event)));
  document.addEventListener('click', (event) => {
    const form = formFromEvent(event);
    if (form) window.setTimeout(() => updateProductForm(form), 0);
  });

  document.addEventListener('variant:change', (event) => {
    // Prestige replaces the buy-buttons block before this event, so the new input contains server-rendered stock.
    window.setTimeout(() => updateProductForm(formFromEvent(event)), 0);
  });

  document.addEventListener('cart:change', () => {
    requestCartSync().catch((error) => console.error('Unable to update _stock_status:', error));
  });

  document.addEventListener(
    'submit',
    async (event) => {
      const form = event.target;
      if (!isCheckoutSubmission(event) || checkoutBypass.has(form)) {
        checkoutBypass.delete(form);
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();

      const submitter = event.submitter || document.activeElement;
      submitter?.setAttribute?.('aria-busy', 'true');

      try {
        await requestCartSync({ refresh: false });
        checkoutBypass.add(form);
        form.requestSubmit(submitter);
      } catch (error) {
        console.error('Unable to verify _stock_status before checkout:', error);
        submitter?.removeAttribute?.('aria-busy');
        window.alert(window.themeVariables?.strings?.stockStatusSyncError || 'Unable to verify product availability. Please try again.');
      }
    },
    true
  );

  // The script is deferred and loaded at the end of body, so the forms are available before DOMContentLoaded.
  document.querySelectorAll('form[action*="/cart/add"]').forEach(updateProductForm);
  if ((window.themeVariables?.cart?.itemCount ?? 0) > 0) {
    requestCartSync().catch((error) => console.error('Unable to update _stock_status:', error));
  }

  const integrationObserver = new MutationObserver((mutations) => {
    let needsCartSync = false;
    let removedContent = false;

    mutations.forEach((mutation) => {
      removedContent ||= mutation.removedNodes.length > 0;

      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;

        if (node.matches('form[action*="/cart/add"]')) updateProductForm(node);
        node.querySelectorAll('form[action*="/cart/add"]').forEach(updateProductForm);

        if (!needsCartSync && (node.matches('cart-drawer, .cart-page, line-item') || node.querySelector('cart-drawer, .cart-page, line-item'))) {
          needsCartSync = true;
        }
      });
    });

    if (removedContent) refreshProductLeadDaysState();
    if (syncState === 'pending' || syncState === 'failed') setSyncState(syncState);

    if (needsCartSync) {
      window.clearTimeout(domCartSyncTimer);
      domCartSyncTimer = window.setTimeout(() => {
        requestCartSync().catch((error) => console.error('Unable to update _stock_status after cart rendering:', error));
      }, 150);
    }
  });
  integrationObserver.observe(document.body, { childList: true, subtree: true });

  window.ATStockStatus = {
    version: integrationVersion,
    get state() {
      return syncState;
    },
    sync: () => requestCartSync(),
  };
})();
